import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  FormControl,
  FormLabel,
  VStack,
  Spinner,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import * as React from 'react';
import {
  MdCancel,
  MdCheckCircle,
  MdOutlineError,
  MdEdit,
  MdDelete,
  MdFilterList,
  MdPerson,
  MdPhone,
  MdLocationOn,
  MdPayment,
  MdAccessTime
} from 'react-icons/md';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  where,
  doc,
  getDoc,
  writeBatch,
  deleteDoc,
  updateDoc,
  getFirestore
} from 'firebase/firestore';
import app from '../../../../config/firebaseConfig'; // Adjust path to your firebase config

// Initialize Firebase app
const db = getFirestore(app);

const columnHelper = createColumnHelper();

// Status options for the update modal
const statusOptions = [
  { value: 'ORDERED', label: 'Ordered', color: 'blue.500', icon: MdOutlineError },
  { value: 'PROCESSING', label: 'Processing', color: 'orange.500', icon: MdOutlineError },
  { value: 'COMPLETED', label: 'Completed', color: 'green.500', icon: MdCheckCircle },
  { value: 'CANCELLED', label: 'Cancelled', color: 'red.500', icon: MdCancel }
];

export default function BookingTable() {
  const [data, setData] = React.useState([]);
  const [sorting, setSorting] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [lastVisible, setLastVisible] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [totalItems, setTotalItems] = React.useState(0);

  // State for the single edit modal
  const [editingRow, setEditingRow] = React.useState(null);
  const [newStatusForEdit, setNewStatusForEdit] = React.useState('');

  const today = new Date();

  // Helper function to format YYYY-MM-DDTHH:MM in local time
  const formatLocalDateTime = (date) => {
    const pad = (n) => n.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const defaultStart = formatLocalDateTime(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0));
  const defaultEnd = formatLocalDateTime(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59));

  const [filters, setFilters] = React.useState({
    startDate: defaultStart,
    endDate: defaultEnd,
    status: '',
    customerName: '',
  });

  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const toast = useToast();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const bgColor = useColorModeValue('white', 'gray.800');

  // Fetch data from Firebase with pagination
  const fetchData = async (isInitialLoad = false) => {
    setLoading(true);
    try {
      if (!db) {
        throw new Error("Firestore is not initialized");
      }

      const bookingsRef = collection(db, 'bookings');
      let constraints = [];

      if (filters.startDate) {
        constraints.push(where('createdAt', '>=', new Date(filters.startDate)));
      }
      if (filters.endDate) {
        constraints.push(where('createdAt', '<=', new Date(filters.endDate)));
      }
      if (filters.status && filters.status.trim() !== '') {
        constraints.push(where('status', '==', filters.status));
      }
      if (filters.customerName && filters.customerName.trim() !== '') {
        constraints.push(where('customerDetails.name', '==', filters.customerName));
      }

      constraints.push(orderBy('updatedAt', 'desc'));
      constraints.push(limit(pageSize));

      if (!isInitialLoad && lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const bookingsQuery = query(bookingsRef, ...constraints);
      const querySnapshot = await getDocs(bookingsQuery);
      const newBookings = [];

      querySnapshot.forEach((doc) => {
        newBookings.push({
          id: doc.id,
          ...doc.data()
        });
      });

      if (isInitialLoad) {
        console.log("Fetched data:", JSON.stringify(newBookings, null, 2));
        setData(newBookings);
        // Get total count for pagination
        let countConstraints = [];
        if (filters.startDate) countConstraints.push(where('createdAt', '>=', new Date(filters.startDate)));
        if (filters.endDate) countConstraints.push(where('createdAt', '<=', new Date(filters.endDate)));
        if (filters.status && filters.status.trim() !== '') countConstraints.push(where('status', '==', filters.status));
        if (filters.customerName && filters.customerName.trim() !== '') countConstraints.push(where('customerDetails.name', '==', filters.customerName));
        
        const countQuery = countConstraints.length > 0 ? query(bookingsRef, ...countConstraints) : bookingsRef;
        const countSnapshot = await getDocs(countQuery);
        setTotalItems(countSnapshot.size);
        setTotalPages(Math.ceil(countSnapshot.size / pageSize));
      } else {
        setData(prevData => [...prevData, ...newBookings]);
      }

      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(!querySnapshot.empty && newBookings.length === pageSize);

    } catch (error) {
      console.error("Error fetching booking data:", error);
      toast({
        title: 'Error fetching data',
        description: error.message || 'Something went wrong while fetching data.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  React.useEffect(() => {
    fetchData(true);
  }, []);

  // Apply filters
  const applyFilters = async () => {
    setLastVisible(null);
    setCurrentPage(1);
    await fetchData(true);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      startDate: defaultStart,
      endDate: defaultEnd,
      status: '',
      customerName: '',
    });
    setLastVisible(null);
    setCurrentPage(1);
    fetchData(true);
  };

  // Handle Edit - opens the modal for a single row
  const handleEdit = (row) => {
    setEditingRow(row);
    // Pre-select the first status that is NOT the current one
    const nextStatus = statusOptions.find(option => option.value !== row.status);
    setNewStatusForEdit(nextStatus ? nextStatus.value : '');
    onEditModalOpen();
  };

  // Submit single update after confirmation
  const submitSingleUpdate = async () => {
    if (!editingRow || !newStatusForEdit) return;

    try {
      const bookingRef = doc(db, 'bookings', editingRow.id);
      await updateDoc(bookingRef, { status: newStatusForEdit });

      toast({
        title: 'Success',
        description: `Booking status updated to ${newStatusForEdit}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onEditModalClose();
      setEditingRow(null);
      setNewStatusForEdit('');
      fetchData(true); // Refresh data
    } catch (err) {
      console.error(err);
      toast({
        title: 'Error',
        description: 'Something went wrong while updating the record',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await deleteDoc(doc(db, 'bookings', id));
        toast({
          title: 'Success',
          description: 'Record deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchData(true); // Refresh data
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Get customer name
  const getCustomerName = (customerDetails) => {
    if (!customerDetails) return 'N/A';
    return customerDetails.name || 'N/A';
  };

  // Get customer phone
  const getCustomerPhone = (customerDetails) => {
    if (!customerDetails) return 'N/A';
    return customerDetails.phone || 'N/A';
  };

  // Get customer address
  const getCustomerAddress = (customerDetails) => {
    if (!customerDetails) return 'N/A';
    return customerDetails.fullAddress || 'N/A';
  };

  // Get status display info
  const getStatusInfo = (status) => {
    const statusInfo = statusOptions.find(option => option.value === status);
    return statusInfo || statusOptions[0];
  };
  
  // Get available status options for the dropdown (excluding current status)
  const getAvailableStatusOptions = (currentStatus) => {
    return statusOptions.filter(option => option.value !== currentStatus);
  };

  const columns = [
    columnHelper.accessor('customerDetails.name', {
      id: 'customerName',
      header: () => (
        <Flex align="center">
          <Icon as={MdPerson} mr={2} />
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            Customer Name
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {getCustomerName(info.row.original.customerDetails)}
        </Text>
      ),
    }),
    columnHelper.accessor('customerDetails.phone', {
      id: 'phoneNumber',
      header: () => (
        <Flex align="center">
          <Icon as={MdPhone} mr={2} />
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            Phone Number
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {getCustomerPhone(info.row.original.customerDetails)}
        </Text>
      ),
    }),
    // columnHelper.accessor('customerDetails.fullAddress', {
    //   id: 'address',
    //   header: () => (
    //     <Flex align="center">
    //       <Icon as={MdLocationOn} mr={2} />
    //       <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
    //         Address
    //       </Text>
    //     </Flex>
    //   ),
    //   cell: (info) => (
    //     <Text color={textColor} fontSize="sm" fontWeight="600" noOfLines={2}>
    //       {getCustomerAddress(info.row.original.customerDetails)}
    //     </Text>
    //   ),
    // }),
    columnHelper.accessor('amount', {
      id: 'amount',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Amount
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          ₹{info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('paymentId', {
      id: 'paymentId',
      header: () => (
        <Flex align="center">
          <Icon as={MdPayment} mr={2} />
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            Payment ID
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700" noOfLines={1}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('createdAt', {
      id: 'timestamp',
      header: () => (
        <Flex align="center">
          <Icon as={MdAccessTime} mr={2} />
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            Date & Time
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {formatTimestamp(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          STATUS
        </Text>
      ),
      cell: (info) => {
        const statusInfo = getStatusInfo(info.getValue());
        return (
          <Flex align="center">
            <Icon
              w="24px"
              h="24px"
              me="5px"
              color={statusInfo.color}
              as={statusInfo.icon}
            />
            <Text color={textColor} fontSize="sm" fontWeight="700">
              {statusInfo.label}
            </Text>
          </Flex>
        );
      },
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          ACTIONS
        </Text>
      ),
      cell: (info) => (
        <Flex>
          <IconButton
            aria-label="Edit Status"
            icon={<MdEdit />}
            size="sm"
            mr={2}
            onClick={() => handleEdit(info.row.original)}
          />
          {/* <IconButton
            aria-label="Delete"
            icon={<MdDelete />}
            size="sm"
            onClick={() => handleDelete(info.row.original.id)}
          /> */}
        </Flex>
      ),
    })
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setLastVisible(null);
    fetchData(true);
  };

  return (
    <>
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Booking Data
          </Text>
        </Flex>

        {/* Filter Section */}
        <Box px="25px" mb="20px">
          <Flex
            gap={3}
            flexWrap="wrap"
            p={4}
            bg={bgColor}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
          >
            <FormControl flex=".5" minW="200px">
              <FormLabel fontSize="sm" mb={1}>Start Date</FormLabel>
              <Input
                type="datetime-local"
                size="sm"
                value={filters.startDate}
                max={filters.endDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </FormControl>

            <FormControl flex=".5" minW="200px">
              <FormLabel fontSize="sm" mb={1}>End Date</FormLabel>
              <Input
                type="datetime-local"
                size="sm"
                max={new Date().toISOString().slice(0, 16)}
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </FormControl>

            <FormControl flex=".5" minW="150px">
              <FormLabel fontSize="sm" mb={1}>Status</FormLabel>
              <Select
                size="sm"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </FormControl>

            {/* <FormControl flex=".5" minW="150px">
              <FormLabel fontSize="sm" mb={1}>Customer Name</FormLabel>
              <Input
                size="sm"
                value={filters.customerName}
                onChange={(e) => setFilters({ ...filters, customerName: e.target.value })}
                placeholder="Enter customer name"
              />
            </FormControl> */}

            <Flex gap={2} flex='.5' alignItems="flex-end">
              <Button
                leftIcon={<MdFilterList />}
                colorScheme="blue"
                size="sm"
                onClick={applyFilters}
              >
                Apply
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Flex>
          </Flex>
        </Box>

        <Box>
          <Table variant="simple" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                    >
                      <Flex
                        justifyContent="space-between"
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.400"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {loading && data.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center" py={8}>
                    <Flex justify="center" align="center">
                      <Spinner size="xl" color="blue.500" thickness="4px" mr={3} />
                      <Text>Loading...</Text>
                    </Flex>
                  </Td>
                </Tr>
              ) : data.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center" py={8}>
                    <Text>No data available</Text>
                  </Td>
                </Tr>
              ) : (
                data.map((row, index) => (
                  <Tr key={row.id}>
                    {table.getRowModel().rows[index]?.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        borderColor="transparent"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination Controls */}
        <Flex
          px="25px"
          pb="20px"
          justifyContent="space-between"
          align="center"
          flexWrap="wrap"
          gap="3"
        >
          <Flex align="center" gap="2">
            <Text fontSize="sm" color={textColor}>
              Showing {data.length} of {totalItems} items
            </Text>
          </Flex>

          <Flex align="center" gap="2">
            <Button size="sm" onClick={() => goToPage(1)} isDisabled={currentPage === 1}>
              First
            </Button>
            <IconButton
              aria-label="Previous page"
              icon={<IoChevronBack />}
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              isDisabled={currentPage === 1}
            />
            <Text fontSize="sm" color={textColor}>
              Page {currentPage} of {totalPages}
            </Text>
            <IconButton
              aria-label="Next page"
              icon={<IoChevronForward />}
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              isDisabled={currentPage === totalPages || !hasMore}
            />
            <Button
              size="sm"
              onClick={() => goToPage(totalPages)}
              isDisabled={currentPage === totalPages || !hasMore}
            >
              Last
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* Single Edit Status Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Booking Status</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {editingRow && (
                <Text>
                  Update status for <strong>{getCustomerName(editingRow.customerDetails)}</strong>
                </Text>
              )}
              <FormControl>
                <FormLabel>Select New Status:</FormLabel>
                <Select
                  value={newStatusForEdit}
                  onChange={(e) => setNewStatusForEdit(e.target.value)}
                  placeholder="Choose a status"
                >
                  {editingRow && getAvailableStatusOptions(editingRow.status).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={submitSingleUpdate}
              isDisabled={!newStatusForEdit}
            >
              Update Status
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}